import type {
  BusinessTravelEntry,
  ElectricityEntry,
  EmployeeCommutingEntry,
  FreightEntry,
  FuelEntry,
  RefrigerantEntry,
  SpendBasedEntry,
  AccommodationEntry,
} from "@/contexts/wizard/types";
import { ACCOMMODATION_FACTORS_BY_COUNTRY } from "@shared/factors/accommodation-factors";

export async function calculateFuelEmissions(
  entry: Partial<FuelEntry>
): Promise<{ tco2e: number | null; energyMwh: number | null }> {
  if (!entry.quantity || entry.quantity <= 0) return { tco2e: null, energyMwh: null };

  try {
    const response = await fetch("/api/emissions/calculate-fuel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        activityType: entry.activityType || "stationary",
        fuelType: entry.fuelType || "diesel",
        quantity: entry.quantity,
        unit: entry.unit || "liters",
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return {
        tco2e: result.totals_tco2e?.total || null,
        energyMwh: result.energyMwh || null,
      };
    }
    return { tco2e: null, energyMwh: null };
  } catch (error) {
    console.error("Error calculating fuel emissions:", error);
    return { tco2e: null, energyMwh: null };
  }
}

export async function calculateElectricityEmissions(entry: Partial<ElectricityEntry>, country?: string): Promise<number | null> {
  if (!entry.consumption || entry.consumption <= 0) return null;

  try {
    const response = await fetch("/api/emissions/calculate-electricity", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        consumption: entry.consumption,
        unit: entry.unit || "kwh",
        // Grid vs Self-Generated split
        gridPercent: entry.gridPercent ?? 100,
        selfGeneratedPercent: entry.selfGeneratedPercent ?? 0,
        isRenewable: entry.isRenewable ?? false,
        country,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.totals_tco2e?.total || null;
    }
    return null;
  } catch (error) {
    console.error("Error calculating electricity emissions:", error);
    return null;
  }
}

export async function calculateRefrigerantEmissions(entry: Partial<RefrigerantEntry>): Promise<number | null> {
  if (!entry.refrigerantType || !entry.leakedAmount || entry.leakedAmount <= 0) return null;

  try {
    const response = await fetch("/api/emissions/calculate-refrigerant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        refrigerantTypeId: entry.refrigerantType,
        leakedAmountKg: entry.leakedAmount,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.totals_tco2e?.total ?? null;
    }
    return null;
  } catch (error) {
    console.error("Error calculating refrigerant emissions:", error);
    return null;
  }
}

export async function calculateBusinessTravelEmissions(entry: Partial<BusinessTravelEntry>): Promise<number | null> {
  if (!entry.distanceKm || entry.distanceKm <= 0 || !entry.mode) return null;

  try {
    const response = await fetch("/api/emissions/calculate-business-travel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mode: entry.mode,
        distanceKm: entry.distanceKm,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.totals_tco2e?.total || null;
    }
    return null;
  } catch (error) {
    console.error("Error calculating business travel emissions:", error);
    return null;
  }
}

export async function calculateAccommodationEmissions(entry: Partial<AccommodationEntry>): Promise<number | null> {
  if (!entry.hotelNights || entry.hotelNights <= 0 || !entry.countryCode) return null;

  const factor = ACCOMMODATION_FACTORS_BY_COUNTRY.find((f) => f.countryCode === entry.countryCode);
  if (!factor) return null;

  // factor.factor is in kgCO2e/room-night, convert to tCO2e
  return (entry.hotelNights * factor.factor) / 1000;
}

export async function calculateEmployeeCommutingEmissions(entry: Partial<EmployeeCommutingEntry>): Promise<number | null> {
  if (!entry.mode) return null;

  try {
    const response = await fetch("/api/emissions/calculate-commuting", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mode: entry.mode,
        distancePerDayKm: entry.distancePerDayKm,
        daysPerWeek: entry.daysPerWeek,
        weeksPerYear: entry.weeksPerYear,
        employeeCount: entry.employeeCount,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.totals_tco2e?.total || null;
    }
    return null;
  } catch (error) {
    console.error("Error calculating employee commuting emissions:", error);
    return null;
  }
}

export async function calculateSpendBasedEmissions(entry: Partial<SpendBasedEntry>): Promise<number | null> {
  if (!entry.subCategory || typeof entry.spendAmount !== "number") return null;
  try {
    const response = await fetch("/api/emissions/calculate-spend-based", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: entry.subCategory, spendAmount: entry.spendAmount }),
    });
    if (!response.ok) return null;
    const result = await response.json();
    return result.totals_tco2e?.total ?? null;
  } catch (error) {
    console.error("Error calculating spend-based emissions:", error);
    return null;
  }
}

export async function calculateFreightEmissions(entry: Partial<FreightEntry>): Promise<number | null> {
  if (!entry.mode || !entry.direction) return null;

  try {
    const response = await fetch("/api/emissions/calculate-freight", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        direction: entry.direction,
        mode: entry.mode,
        weightTonnes: entry.weightTonnes,
        distanceKm: entry.distanceKm,
        tonneKm: entry.tonneKm,
        parcelCount: entry.parcelCount,
        unit: entry.unit,
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.totals_tco2e?.total || null;
    }
    return null;
  } catch (error) {
    console.error("Error calculating freight emissions:", error);
    return null;
  }
}

export interface WasteEmissionsResult {
  total: number;
  breakdown: {
    landfillTco2e: number;
    recycledTco2e: number;
    compostingTco2e: number;
    incinerationTco2e: number;
    ewasteTco2e: number;
    hazardousTco2e: number;
  };
}

export async function calculateWasteEmissions(data: {
  landfillTonnes?: number | null;
  recycledTonnes?: number | null;
  compostingTonnes?: number | null;
  incinerationTonnes?: number | null;
  ewasteTonnes?: number | null;
  hazardousTonnes?: number | null;
}): Promise<WasteEmissionsResult | null> {
  try {
    const response = await fetch("/api/emissions/calculate-waste", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (response.ok) {
      const result = await response.json();
      return {
        total: result.totals_tco2e?.total ?? 0,
        breakdown: result.breakdown,
      };
    }
    return null;
  } catch (error) {
    console.error("Error calculating waste emissions:", error);
    return null;
  }
}
